import axios from 'axios';
import { errorMessage, isUnavailable, vpApi } from '../../api/velopulse';
import { mergeStatusAndCapabilities } from './assistantFeatureModel';

const data = (p) => p.then((r) => r.data);
const MESSAGE_TIMEOUT = 280000;
const FILE_TIMEOUT = 120000;
const ASSISTANT_DOWNLOAD_PATH = /^\/api\/assistant\/files\/([A-Za-z0-9_-]{1,80})\/download$/;

function looksLikeSecret(text) {
  return /api[_-]?key|token=|bearer\s+[a-z0-9]|sk-[a-z0-9]/i.test(String(text || ''));
}

function httpCode(err) {
  return err?.response?.data?.code || err?.response?.data?.error_code || '';
}

function httpErrorText(err) {
  const text = err?.response?.data?.error || err?.response?.data?.message || '';
  return typeof text === 'string' ? text : '';
}

export function assistantToolErrorMessage(err, fallback = '這項操作沒有完成。') {
  const status = err?.response?.status;
  const code = httpCode(err);
  const text = httpErrorText(err);
  const configured = err?.response?.data?.configured;
  if (
    status === 503
    && (
      code === 'IMAGE_NOT_CONFIGURED'
      || code === 'IMAGE_PROVIDER_NOT_CONFIGURED'
      || code === 'not_configured'
      || configured === false
      || /not_configured|未設定.*(圖像|圖片|image)|圖片生成功能尚未設定/i.test(text)
    )
  ) {
    return '圖像產生未設定供應商或金鑰，因此無法產生圖像。這個環境沒有改用其他供應商。';
  }
  if (status === 403 && (code === 'FILE_FORBIDDEN' || code === 'ATTACHMENT_DENIED' || /檔案|附件|file/i.test(text))) {
    return '沒有權限使用這個檔案，或檔案不屬於目前組織。';
  }
  if (status === 404 && (code === 'FILE_NOT_FOUND' || /檔案|附件|file|對話/i.test(text))) {
    return '找不到這個檔案，或它不屬於目前的登入與組織範圍。';
  }
  if (
    (status === 400 || status === 422)
    && (code === 'SOURCE_ERROR' || code === 'EXTRACTION_FAILED' || /擷取|提取|來源/i.test(text))
  ) {
    return '檔案沒有可用的提取來源，因此不會當成證據。';
  }
  if (status === 409 && (code === 'STALE_CONVERSATION' || code === 'STALE_ORG')) {
    return '這則對話或組織已切換，回覆沒有寫入目前畫面。';
  }
  if (err?.code === 'STALE_ORG' || err?.code === 'STALE_CONVERSATION') {
    return '組織或對話已切換，沒有下載檔案。';
  }
  if (err?.code === 'BLOCKED_DOWNLOAD_URL') {
    return '這個下載位址不是萬事通檔案路徑，已拒絕。';
  }
  const mapped = errorMessage(err, fallback);
  if (looksLikeSecret(mapped)) return fallback;
  return mapped;
}

export async function getAssistantCapabilities() {
  return data(axios.get('/api/assistant/capabilities'));
}

export async function loadAssistantFeatures() {
  const status = await vpApi.getAssistantStatus();
  try {
    const extra = await getAssistantCapabilities();
    return mergeStatusAndCapabilities(status, extra);
  } catch (err) {
    const s = err?.response?.status;
    if (isUnavailable(err) || s === 404 || s === 501 || !err?.response) return status;
    if (s === 401 || s === 403) throw err;
    return status;
  }
}

export function sendAssistantMessageWithTools({
  text,
  conversationId,
  attachmentIds,
  mode,
  researchResume,
}) {
  const body = { text, conversation_id: conversationId };
  if (Array.isArray(attachmentIds) && attachmentIds.length) {
    body.attachment_ids = attachmentIds.filter((id) => typeof id === 'string' && id.trim()).map((id) => id.trim());
  }
  if (mode === 'research' || mode === 'chat') body.mode = mode;
  if (researchResume === true) body.research_resume = true;
  return data(axios.post('/api/assistant/messages', body, { timeout: MESSAGE_TIMEOUT }));
}

function originalFileName(file) {
  const name = typeof file?.name === 'string' ? file.name.trim() : '';
  return name || 'upload.bin';
}

export async function uploadAssistantFiles(fileList, { maxBytes = 0 } = {}) {
  const files = Array.from(fileList || []).filter(Boolean);
  const uploaded = [];
  const failures = [];
  for (const file of files) {
    const name = originalFileName(file);
    if (Number.isFinite(Number(maxBytes)) && Number(maxBytes) > 0 && Number(file?.size) > Number(maxBytes)) {
      const error = new Error(`檔案超過伺服器公告的 ${Number(maxBytes)} bytes 上限。`);
      error.response = { status: 413, data: { error: error.message } };
      failures.push({ name, error });
      continue;
    }
    const fd = new FormData();
    fd.append('file', file, name);
    try {
      const payload = await data(axios.post('/api/assistant/files', fd, { timeout: FILE_TIMEOUT }));
      uploaded.push(payload);
    } catch (error) {
      failures.push({ name, error });
    }
  }
  return { uploaded, failures };
}

export function listAssistantFiles() {
  return data(axios.get('/api/assistant/files'));
}

export function listAssistantConversations({ limit = 30, before } = {}) {
  const params = { limit };
  if (before) params.before = before;
  return data(axios.get('/api/assistant/conversations', { params }));
}

export function listAssistantConversationMessages(conversationId, { limit = 50, before } = {}) {
  const params = { limit };
  if (before) params.before = before;
  return data(axios.get(`/api/assistant/conversations/${encodeURIComponent(conversationId)}/messages`, { params }));
}

export function createAssistantArtifact(payload) {
  return data(axios.post('/api/assistant/artifacts', payload, { timeout: FILE_TIMEOUT }));
}

export function runAssistantAnalysis(payload) {
  return data(axios.post('/api/assistant/analysis', payload, { timeout: FILE_TIMEOUT }));
}

export function generateAssistantImage(payload) {
  return data(axios.post('/api/assistant/images', payload, { timeout: FILE_TIMEOUT }));
}

export function assistantFileDownloadPath(fileId) {
  if (typeof fileId !== 'string') return '';
  const id = fileId.trim();
  if (!id || id.length > 80 || !/^[A-Za-z0-9_-]+$/.test(id)) return '';
  return `/api/assistant/files/${id}/download`;
}

export function resolveAssistantDownloadPath(value) {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (!raw || raw.length > 2000) return '';
  if (raw.startsWith('/')) {
    const path = raw.split('?')[0].split('#')[0];
    return ASSISTANT_DOWNLOAD_PATH.test(path) ? path : '';
  }
  try {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
    const parsed = new URL(raw, origin || undefined);
    if (!origin || parsed.origin !== origin) return '';
    if (parsed.username || parsed.password) return '';
    if (parsed.search || parsed.hash) return '';
    return ASSISTANT_DOWNLOAD_PATH.test(parsed.pathname) ? parsed.pathname : '';
  } catch {
    return '';
  }
}

function currentOrgHeader() {
  return axios.defaults.headers.common['X-Org-Id'] || axios.defaults.headers.common['x-org-id'] || '';
}

async function parseBlobHttpError(err) {
  const payload = err?.response?.data;
  if (payload instanceof Blob) {
    try {
      const text = await payload.text();
      const json = JSON.parse(text);
      if (json && typeof json === 'object') err.response.data = json;
    } catch {
      /* keep blob */
    }
  }
  return err;
}

export async function downloadAssistantBlob({
  path,
  filename,
  orgId,
  conversationId,
  currentConversationId,
  getLiveConversationId,
} = {}) {
  const safePath = resolveAssistantDownloadPath(path);
  if (!safePath) {
    const error = new Error('這個下載位址不是萬事通檔案路徑，已拒絕。');
    error.code = 'BLOCKED_DOWNLOAD_URL';
    throw error;
  }
  const liveOrg = currentOrgHeader();
  if (orgId && liveOrg && String(liveOrg) !== String(orgId)) {
    const error = new Error('組織已切換，沒有下載檔案。');
    error.code = 'STALE_ORG';
    throw error;
  }
  const liveConversation = typeof getLiveConversationId === 'function'
    ? getLiveConversationId()
    : currentConversationId;
  if (conversationId && liveConversation && String(conversationId) !== String(liveConversation)) {
    const error = new Error('對話已切換，沒有下載檔案。');
    error.code = 'STALE_CONVERSATION';
    throw error;
  }
  let payload;
  try {
    payload = await axios.get(safePath, { responseType: 'blob', timeout: FILE_TIMEOUT });
  } catch (err) {
    throw await parseBlobHttpError(err);
  }
  const status = payload?.status;
  if (status && (status < 200 || status >= 300)) {
    const error = new Error('檔案下載失敗。');
    error.response = { status, data: payload?.data };
    throw await parseBlobHttpError(error);
  }
  const blob = payload?.data;
  if (!(blob instanceof Blob)) {
    throw new Error('伺服器沒有回傳可下載的檔案內容。');
  }
  const orgAfter = currentOrgHeader();
  if (orgId && orgAfter && String(orgAfter) !== String(orgId)) {
    const error = new Error('組織已切換，沒有下載檔案。');
    error.code = 'STALE_ORG';
    throw error;
  }
  const conversationAfter = typeof getLiveConversationId === 'function'
    ? getLiveConversationId()
    : currentConversationId;
  if (conversationId && conversationAfter && String(conversationId) !== String(conversationAfter)) {
    const error = new Error('對話已切換，沒有下載檔案。');
    error.code = 'STALE_CONVERSATION';
    throw error;
  }
  const name = (typeof filename === 'string' && filename.trim()) ? filename.trim() : 'download';
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
  return { name, path: safePath };
}
